import {Controller} from "@hotwired/stimulus"

/**
 * Device Contacts Controller
 *
 * This controller handles integration with the device's contacts for PWA and native app contexts.
 * It provides functionality to:
 * 1. Import contacts from the device's contact list
 * 2. Export vCards directly to the device's contacts
 *
 * It uses the Web Contacts API when available and provides fallbacks when it's not.
 */
export default class extends Controller {
    static targets = ["importButton", "exportButton"]

    connect() {
        // Check if the Contacts API is supported
        this.checkContactsApiSupport()
    }

    /**
     * Check if the Contacts API is supported and update UI accordingly
     */
    checkContactsApiSupport() {
        const isContactsApiSupported = 'contacts' in navigator && 'ContactsManager' in window
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent)

        // Only show device contacts features on mobile devices
        if (isMobile) {
            // If we have import button target, configure it
            if (this.hasImportButtonTarget) {
                if (isContactsApiSupported) {
                    this.importButtonTarget.classList.remove('hidden')
                } else {
                    // Show a fallback button that explains the limitation
                    this.importButtonTarget.classList.remove('hidden')
                    this.importButtonTarget.setAttribute('data-action', 'device-contacts#showContactsApiNotSupported')
                }
            }

            // If we have export button target, configure it
            if (this.hasExportButtonTarget) {
                this.exportButtonTarget.classList.remove('hidden')
            }
        }
    }

    /**
     * Show a message explaining that the Contacts API is not supported
     */
    showContactsApiNotSupported(event) {
        event.preventDefault()

        // Detect the device/browser for a more specific message
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
        const isAndroid = /Android/i.test(navigator.userAgent)

        let message = 'Your device or browser does not support direct contact access. '

        if (isIOS) {
            message += 'Safari and other iOS browsers do not support the Web Contacts API. '
        } else if (isAndroid) {
            message += 'Please try using Chrome for Android (version 80+) which supports this feature. '
        }

        message += 'Please use the file upload option instead.'

        alert(message)
    }

    /**
     * Import contacts from the device's contact list
     */
    async importFromDevice(event) {
        event.preventDefault()

        if (!('contacts' in navigator && 'ContactsManager' in window)) {
            this.showContactsApiNotSupported(event)
            return
        }

        try {
            // Request contacts from the device
            const props = ['name', 'email', 'tel', 'address']
            const opts = {multiple: false}

            const contacts = await navigator.contacts.select(props, opts)

            if (contacts.length > 0) {
                const contact = contacts[0]

                // Convert the contact to vCard format
                const vcard = this.contactToVCard(contact)

                // Submit the vCard data to the server
                this.submitVCardData(vcard)
            }
        } catch (error) {
            console.error('Error accessing contacts:', error)
            alert('There was an error accessing your contacts. Please try again or use the file upload option.')
        }
    }

    /**
     * Convert a contact from the Contacts API to vCard format
     */
    contactToVCard(contact) {
        let vcard = 'BEGIN:VCARD\nVERSION:3.0\n'

        // Add name
        if (contact.name && contact.name.length > 0) {
            const fullName = contact.name[0]
            const nameParts = fullName.split(' ')
            const lastName = nameParts.pop() || ''
            const firstName = nameParts.join(' ') || ''

            vcard += `N:${lastName};${firstName};;;\n`
            vcard += `FN:${fullName}\n`
        }

        // Add email
        if (contact.email && contact.email.length > 0) {
            vcard += `EMAIL:${contact.email[0]}\n`
        }

        // Add phone
        if (contact.tel && contact.tel.length > 0) {
            vcard += `TEL:${contact.tel[0]}\n`
        }

        // Add address
        if (contact.address && contact.address.length > 0) {
            const address = contact.address[0]
            vcard += `ADR:;;${address};;;\n`
        }

        vcard += 'END:VCARD'
        return vcard
    }

    /**
     * Submit vCard data to the server
     */
    submitVCardData(vcardData) {
        // Create a file from the vCard data
        const file = new File([vcardData], 'contact.vcf', {type: 'text/vcard'})

        // Create a FormData object
        const formData = new FormData()
        formData.append('vcard_file', file)

        // Submit the form data to the server
        fetch('/members/import_vcard', {
            method: 'POST',
            body: formData,
            headers: {
                'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
            }
        })
            .then(response => {
                if (response.ok) {
                    window.location.reload()
                } else {
                    alert('There was an error importing the contact. Please try again.')
                }
            })
            .catch(error => {
                console.error('Error submitting vCard:', error)
                alert('There was an error importing the contact. Please try again.')
            })
    }

    /**
     * Add a vCard to the device's contacts
     */
    async addToDeviceContacts(event) {
        event.preventDefault()

        const vcardUrl = event.currentTarget.getAttribute('href')

        // If the Web Contacts API is supported, we could use it here
        // However, there's currently no standard way to add contacts to the device
        // So we'll use a different approach

        // For iOS devices, we can use the "add to contacts" URL scheme
        if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
            window.location.href = vcardUrl
            alert('After downloading the vCard, you can open it to add to your contacts.')
            return
        }

        // For Android devices, we can download the vCard and provide instructions
        if (/Android/i.test(navigator.userAgent)) {
            window.location.href = vcardUrl
            alert('After downloading the vCard, you can open it to add to your contacts.')
            return
        }

        // For desktop or unsupported devices, just download the vCard
        window.location.href = vcardUrl
    }
}
